import { useCallback, useEffect, useState } from 'react'
import { HiExclamationTriangle, HiMiniPlus, HiArrowLongRight, HiArrowLongLeft } from 'react-icons/hi2'
import { RiCheckboxBlankFill } from 'react-icons/ri'

import { useIntl, FormattedMessage } from 'react-intl'

import { CheckBox } from './CheckBox'
import { Select } from './Select'

import Evaluation360 from '../../API/Evaluation/Evaluation360'
import Lists from '../../API/Lists/Lists'

import { evaluation360 } from '../../i18n/translatedMessages'
import { translate } from '../../i18n/Library/evaluations'
import { translate as translateAreas } from '../../i18n/Library/areasCorrelation'
import { translate as translateLevels } from '../../i18n/Library/hierarchicalLevel'

import countryMapper from '../../Utils/CountriesMapper.js'
import areasMapper from '../../Utils/AreasMapper.js'
import levelsMapper from '../../Utils/LevelsMapper.js'
import { Tooltip } from './Tooltip'

export function CreateParams({ setShow, load }) {
	const getLanguage = useIntl()
	const language = getLanguage.locale === 'PT-BR' ? 'pt' : 'es'

	const [allEmployeesSelected, setAllEmployeesSelected] = useState(false)

	const [step, setStep] = useState(1)
	const [param, setParam] = useState(null)
	const [template, setTemplate] = useState(null)
	const [loading, setLoading] = useState(true)
	const [criterias, setCriterias] = useState(null)

	const [choiseCriterias, setChoiseCriterias] = useState([])
	const [choiseEmployee, setChoiseEmployee] = useState([])
	const [error, setError] = useState(false)
	const [employees, setEmployees] = useState(null)
	const [lists, setLists] = useState(null)

	const [filter, setFilter] = useState({
		area: 0,
		country: 0,
		level: 0,
		name: '',
	})

	const loadData = useCallback(async () => {
		try {
			setLoading(true)
			const { id } = JSON.parse(localStorage.getItem('user'))
			const [allCriterias, areas, countries, levels] = await Promise.all([
				Evaluation360.allCriteriaByCompany(id),
				Lists.areas(),
				Lists.countries(),
				Lists.levels(),
			])

			setLists({
				areas: areas.map(areasMapper.toDomain),
				countries: countries.map(countryMapper.toDomain),
				levels: levels.map(levelsMapper.toDomain),
			})

			setCriterias(allCriterias)
		} catch (error) {
			console.error('MODAL EVALUATION 360: ', error)
		} finally {
			setLoading(false)
		}
	}, [])

	const loadEmployees = useCallback(async () => {
		try {
			let query = '?'

			if (filter.name !== '') {
				query = `${query}&query=${filter.name}`
			}

			if (filter.area !== 0) {
				query = `${query}&area=${filter.area}`
			}
			if (filter.country !== 0) {
				query = `${query}&country=${filter.country}`
			}
			if (filter.level !== 0) {
				query = `${query}&level=${filter.level}`
			}

			const { id } = JSON.parse(localStorage.getItem('user'))
			const allEmployees = await Evaluation360.allEmployees(id, query)

			setEmployees(allEmployees)
		} catch (error) {
			console.error('MODAL EVALUATION 360: ', error)
		}
	}, [filter])

	useEffect(() => {
		loadData()
	}, [loadData])

	useEffect(() => {
		loadEmployees()
	}, [loadEmployees])

	const calcDate = (init, end) => {
		const date1 = new Date(init)
		const date2 = new Date(end)
		return date1.getTime() >= date2.getTime()
	}

	const clean = () => {
		setFilter({
			area: 0,
			country: 0,
			level: 0,
			name: '',
		})
	}

	const handleChange = event => {
		setParam({ ...param, [event.target.name]: event.target.value })
	}

	const handleChangeTemplate = event => {
		setTemplate({ ...template, [event.target.name]: event.target.value })
	}

	const choiseParams = event => {
		if (event.checked) {
			setChoiseCriterias([...choiseCriterias, event.id])
		} else {
			const aux = choiseCriterias.filter(item => item !== event.id)
			setChoiseCriterias(aux)
		}
	}

	const handleFilterChange = event => {
		setFilter({
			...filter,
			[event.target.name]: event.target.value,
		})
	}

	const handleChoiseEmployee = event => {
		if (event.checked) {
			setChoiseEmployee([...choiseEmployee, event.id])
		} else {
			const aux = choiseEmployee.filter(item => item !== event.id)
			setChoiseEmployee(aux)
		}
	}

	const handleCreateParam = async () => {
		const user = JSON.parse(localStorage.getItem('user'))
		const body = {
			name: param.name,
			description: param.description,
			companyId: user.id,
		}

		await Evaluation360.createCriteria(body)

		setParam(null)
		loadData()
	}

	const handleCreateTemplate = async () => {
		const { id } = JSON.parse(localStorage.getItem('user'))

		const body = {
			name: template.name,
			companyId: id,
			initDate: template.start,
			endDate: template.end,
			criteria: choiseCriterias,
			employees: choiseEmployee,
		}

		await Evaluation360.createTemplate(body)

		setTemplate(null)
		setChoiseCriterias([])
		setChoiseEmployee([])
		load()
		setShow(false)
	}

	const next = () => {
		if (choiseCriterias.length < 3 || choiseCriterias.length > 10 || !template?.name || !template?.start || !template?.end) {
			setError(true)
			return
		}

		if (calcDate(template.start, template.end)) {
			setError(true)
			return
		}

		setStep(2)
		setError(false)
	}

	const selectAll = event => {
		if (event.checked) {
			setChoiseEmployee(employees.filter(item => item.template.status === 'inactive').map(item => item.id))
		} else {
			setChoiseEmployee([])
		}
		setAllEmployeesSelected(event.checked)
	}

	if (loading) {
		return (
			<div className='tw-w-[100%] tw-h-full tw-flex tw-items-center tw-justify-center'>
				<span className='tw-text-xl tw-font-bold tw-animate-pulse tw-gray-600'>{evaluation360.loading}</span>
			</div>
		)
	}

	return (
		<div>
			<div className='tw-p-6 tw-pb-1 tw-mb-2 border-bottom'>
				<h4 className='tw-font-bold'>{evaluation360.btnCreateEvaluation}</h4>

				<p>{evaluation360.defineParamsDescription}</p>
			</div>

			{step === 1 && (
				<div className='tw-p-4'>
					<div className='tw-gap-4'>
						<div className='tw-grid tw-grid-cols-4 tw-items-center tw-gap-2'>
							<div className='tw-col-span-2'>
								<h5 className='tw-text-base tw-font-bold tw-m-0 tw-p-0'>{evaluation360.labelName}</h5>
								<input
									type='text'
									value={template?.name || ''}
									className='tw-w-full tw-mt-0'
									style={{ borderRadius: 4, margin: 0, border: `1px solid ${error && !template?.name ? '#f43f5e' : '#dee2e6'}` }}
									onChange={handleChangeTemplate}
									name='name'
								/>
								<div className='tw-h-6'>
									{error && !template?.name && <span className='tw-text-error-500 tw-text-sm'>{evaluation360.errorName}</span>}
								</div>
							</div>
							<div className='tw-col-span-2'>
								<div className='tw-gap-2'>
									<h5 className='tw-text-base tw-font-bold tw-m-0 tw-p-0'>{evaluation360.paramPeriodTitle}</h5>
									<div className='tw-w-full'>
										<div className='tw-flex tw-gap-2 tw-items-center'>
											<span>{evaluation360.paramInitalDate}</span>
											<div className=''>
												<input
													value={template?.start || ''}
													onChange={handleChangeTemplate}
													name='start'
													type='date'
													className='tw-w-full tw-h-[36px] tw-px-2'
													style={{
														borderRadius: 4,
														border: `1px solid ${error && !template?.start ? '#f43f5e' : '#dee2e6'}`,
													}}
												/>

												{error && !template?.start && <span className='tw-text-error-500 tw-text-sm'>{evaluation360.errorInput}</span>}
											</div>
											<span>{evaluation360.endDate}</span>
											<div>
												<input
													value={template?.end || ''}
													onChange={handleChangeTemplate}
													name='end'
													type='date'
													className='tw-w-full tw-h-[36px] tw-px-2'
													style={{
														borderRadius: 4,
														border: `1px solid ${error && !template?.end ? '#f43f5e' : '#dee2e6'}`,
													}}
												/>

												{error && !template?.end && <span className='tw-text-error-500 tw-text-sm'>{evaluation360.errorInput}</span>}
											</div>
										</div>
										{!error && <div className='tw-h-6'></div>}
										{error && calcDate(template?.start, template?.end) && (
											<div className='tw-text-error-500 tw-text-sm tw-w-full tw-text-center'>{evaluation360.errorDate}</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<div className='tw-grid tw-grid-cols-4 tw-items-center tw-gap-2 tw-mt-4'>
							<div>
								<h5 className='tw-text-base tw-font-bold'>Critérios</h5>
							</div>
							<div className='tw-col-span-3'>
								<h5 className='tw-text-base tw-font-bold'>{evaluation360.paramCriteriasDescription} </h5>
							</div>
						</div>
						<ul className='tw-p-0 tw-m-0 tw-list-none tw-overflow-auto tw-h-[290px] tw-mb-2 tw-px-2'>
							{[...criterias]
								.sort((a, b) => a.name.localeCompare(b.name))
								.map(item => (
									<li key={item.id} className='tw-grid tw-grid-cols-4 tw-items-center tw-gap-2 tw-py-3 border-bottom'>
										<div className='tw-flex tw-gap-2'>
											<CheckBox onClick={choiseParams} id={item.id} isChecked={choiseCriterias.includes(item.id)} />{' '}
											{translate[language][item.name] || item.name}
										</div>
										<div className='tw-col-span-3'>{translate[language][item.description] || item.description}</div>
									</li>
								))}
							<li className='tw-grid tw-grid-cols-4 tw-items-center tw-gap-2 tw-pt-3 tw-pb-2 '>
								<div className='tw-flex tw-gap-2'>
									<FormattedMessage id='inputNewCriteria'>
										{msg => (
											<input
												type='text'
												name='name'
												onChange={handleChange}
												className='border tw-w-full'
												style={{ borderRadius: 4 }}
												placeholder={msg}
											/>
										)}
									</FormattedMessage>
								</div>
								<div className='tw-col-span-3 tw-flex tw-gap-2 tw-items-center'>
									<FormattedMessage id='inputDescription'>
										{msg => (
											<input
												type='text'
												name='description'
												onChange={handleChange}
												className='border tw-flex tw-w-full'
												style={{ borderRadius: 4 }}
												placeholder={msg}
											/>
										)}
									</FormattedMessage>
									<button
										onClick={handleCreateParam}
										className='border-0 tw-w-10 tw-h-9 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-text-purple tw-bg-purple-100 hover:tw-bg-purple hover:tw-text-white active:tw-bg-purple-600'
									>
										<HiMiniPlus size={22} />
									</button>
								</div>
							</li>
						</ul>
						<div className='tw-flex tw-items-center tw-py-2 tw-justify-between border-top'>
							<div className='tw-flex tw-gap-1 tw-items-center'>
								<span className='tw-font-bold tw-text-purple'>{evaluation360.labelStep} 1</span>
								<span className='tw-text-gray-400'>{evaluation360.labelBy} 2</span>
							</div>
							{choiseCriterias.length < 3 && error && (
								<div className='tw-flex tw-items-center tw-gap-1 tw-text-[#F49E0B] '>
									<HiExclamationTriangle size={20} />
									{evaluation360.errorAddMinParams}
								</div>
							)}
							{choiseCriterias.length > 10 && error && (
								<div className='tw-flex tw-items-center tw-gap-1 tw-text-error-500 '>
									<HiExclamationTriangle size={20} />
									{evaluation360.errorAddMaxParams}
								</div>
							)}
							<div className='tw-flex tw-items-center tw-gap-2 tw-px-2'>
								<button className='border tw-rounded-full tw-px-3 tw-py-1 tw-bg-white hover:tw-bg-gray-100' onClick={() => setShow(false)}>
									Cancelar
								</button>
								<button
									disabled={false}
									onClick={next}
									className='border tw-rounded-full tw-px-3 tw-py-1 tw-bg-purple-500 tw-text-white disabled:tw-bg-gray-200 disabled:tw-text-gray-400 hover:tw-bg-purple-700 active:tw-bg-purple-700'
								>
									{evaluation360.nextStepEvaluation}
									<HiArrowLongRight size={20} />
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
			{step === 2 && (
				<div>
					<div className='tw-p-4 tw-pt-3'>
						{lists && (
							<div className='tw-flex tw-gap-2 tw-items-center tw-h-[36px] tw-mb-5'>
								<input
									type='text'
									value={filter.name}
									className='tw-h-[32px] tw-w-2/6 border'
									placeholder='Buscar Colaborador'
									style={{ borderRadius: 6 }}
									onChange={handleFilterChange}
									name='name'
								/>
								<Select
									list={lists.countries}
									option={filter.country}
									onChange={handleFilterChange}
									placeholder='País'
									name='country'
									className='tw-w-1/6'
								/>
								<Select list={lists.areas} option={filter.area} onChange={handleFilterChange} placeholder='Area' name='area' className='tw-w-1/6' />
								<Select
									list={lists.levels}
									option={filter.level}
									onChange={handleFilterChange}
									placeholder={evaluation360.formLevel}
									name='level'
									className='tw-w-[200px]'
								/>
								<button onClick={clean} className='tw-h-[38px] tw-bg-white tw-w-1/6 border tw-rounded-md'>
									{evaluation360.btnClearFilter}
								</button>
							</div>
						)}
						<div className='tw-grid tw-grid-cols-7 tw-gap-4 tw-bg-[#F1F4F9] tw-py-2 tw-px-3 tw-rounded-md tw-mb-2'>
							<div className='tw-flex tw-items-center tw-font-bold tw-text-sm tw-gap-2 tw-col-span-2'>
								<CheckBox onClick={selectAll} isChecked={allEmployeesSelected} />
								Colaborador
							</div>
							<div className='tw-flex tw-items-center tw-font-bold tw-text-sm tw-gap-2'>{evaluation360.titleCountry}</div>
							<div className='tw-flex tw-items-center tw-font-bold tw-text-sm tw-gap-2'>{evaluation360.titleArea}</div>
							<div className='tw-flex tw-items-center tw-font-bold tw-text-sm tw-gap-2'>{evaluation360.titleLevel}</div>
							<div className='tw-flex tw-items-center tw-font-bold tw-text-sm tw-gap-2 tw-col-span-2'>{evaluation360.titleTemplate}</div>
						</div>

						<ul className='tw-p-0 tw-m-0 tw-space-y-2 tw-h-[280px] tw-overflow-auto'>
							{employees?.map(item => (
								<li key={item.id} className='tw-grid tw-grid-cols-7 tw-gap-4 tw-py-4 tw-px-3 tw-rounded-md hover:tw-bg-[#EFF1FE]'>
									<div className='tw-flex tw-gap-2 tw-items-center tw-col-span-2'>
										{item.template.status === 'active' ? (
											<Tooltip text={evaluation360.tooltipEmployeeInactive}>
												<div className='tw-text-gray-400'>
													<RiCheckboxBlankFill size={24} />
												</div>
											</Tooltip>
										) : (
											<CheckBox onClick={handleChoiseEmployee} id={item.id} isChecked={choiseEmployee.includes(item.id)} />
										)}
										<img
											src='https://assets.mydna.company/profile-placeholder.png'
											alt=''
											className='img-fluid rounded-circle tw-w-[40px] tw-h-[40px]'
										/>
										<span className='tw-font-bold'>{item.name}</span>
									</div>
									<div className='tw-flex tw-items-center tw-text-gray-500'>
										{' '}
										<p className='tw-break-all'>{item.country.label}</p>
									</div>
									<div className='tw-flex tw-items-center tw-text-gray-500'>
										{' '}
										<p className='tw-break-all'>{translateAreas[language][item.area.label]}</p>
									</div>
									<div className='tw-flex tw-items-center tw-text-gray-500'>
										{' '}
										<p className='tw-break-all'>{translateLevels[language][item.level.label]}</p>
									</div>
									<div className='tw-flex tw-items-center tw-col-span-2'>
										{item.template.status === 'active' ? (
											<div className='tw-px-3  tw-py-1 tw-text-center tw-bg-[#E2E4FD] tw-text-purple tw-rounded-full tw-inline-flex '>
												{item.template.name}
											</div>
										) : (
											<div className='tw-px-3  tw-py-1 tw-text-center tw-bg-gray-100 tw-text-gray-500 tw-rounded-full tw-inline-flex '>
												{evaluation360.labelNoDefinedEvaluation}
											</div>
										)}
									</div>
								</li>
							))}
						</ul>
					</div>
					<div className='tw-flex tw-items-center tw-py-2 tw-justify-between tw-px-6 border-top'>
						<div className='tw-flex tw-gap-1 tw-items-center'>
							<span className='tw-font-bold tw-text-purple'>{evaluation360.labelStep} 2</span>
							<span className='tw-text-gray-400'>{evaluation360.labelBy} 2</span>
						</div>
						<div className='tw-flex tw-items-center tw-gap-2 tw-justify-end'>
							<button
								onClick={() => setStep(1)}
								className='border tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-px-4 tw-py-1 tw-bg-transparent hover:tw-bg-gray-100 active:tw-bg-gray-200'
							>
								<HiArrowLongLeft />
								{evaluation360.backStepEvaluation}
							</button>
							<button
								onClick={handleCreateTemplate}
								className='border tw-rounded-full tw-px-4 tw-py-1 tw-bg-purple hover:tw-bg-purple-700 active:tw-bg-purple-700 tw-text-white'
							>
								{evaluation360.btnCreateEvaluation}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
